// IE Hub - Quick check Component
// Displays conditional validation warnings with clickable text segments

import React from 'react';
import { Box, Typography, Link, useTheme, EditIcon } from '@/ui';
import type { OpenActionSpec } from '@/shared/warnings/types';

interface ClickableSegment {
  text: string;
  action: 'openHelpDialog' | 'openNotesDialog' | 'focusField' | 'openSchemaDialog' | 'openFieldEdit' | 'openWarningAction';
  targetFieldName?: string | undefined;
  targetSchemaFieldName?: string | undefined;
  targetApiFieldName?: string | undefined;
  schemaKey?: string | undefined; // for openSchemaDialog and openFieldEdit
  openAction?: OpenActionSpec | undefined;
  varId?: string | undefined;
  showEditIcon?: boolean | undefined;
}

interface QuickCheckProps {
  message: string;
  segments: ClickableSegment[];
  onHelpDialogOpen?: (() => void) | undefined;
  onNotesDialogOpen?: (() => void) | undefined;
  onFieldFocus?: ((fieldName: string) => void) | undefined;
  onOpenSchemaDialog?: ((schemaKey: string) => void) | undefined;
  onOpenFieldEdit?: ((schemaKey: string, fieldName: string, apiFieldName: string) => void) | undefined;
  onOpenWarningAction?: ((action: OpenActionSpec, metadata?: { varId?: string }) => void) | undefined;
}

const QuickCheck: React.FC<QuickCheckProps> = ({
  message,
  segments,
  onHelpDialogOpen,
  onNotesDialogOpen,
  onFieldFocus,
  onOpenSchemaDialog,
  onOpenFieldEdit,
  onOpenWarningAction,
}) => {
  const theme = useTheme();

  const findSegmentIndex = (haystack: string, needle: string): number => {
    if (!haystack || !needle) return -1;
    const exact = haystack.indexOf(needle);
    if (exact !== -1) return exact;
    return haystack.toLowerCase().indexOf(needle.toLowerCase());
  };

  const handleSegmentClick = (segment: ClickableSegment) => {
    switch (segment.action) {
      case 'openHelpDialog':
        onHelpDialogOpen?.();
        break;
      case 'openNotesDialog':
        onNotesDialogOpen?.();
        break;
      case 'focusField':
        if (segment.targetFieldName) {
          onFieldFocus?.(segment.targetFieldName);
        }
        break;
      case 'openSchemaDialog':
        if (segment.schemaKey) {
          onOpenSchemaDialog?.(segment.schemaKey);
        }
        break;
      case 'openFieldEdit':
        if (segment.schemaKey && segment.targetSchemaFieldName && segment.targetApiFieldName) {
          onOpenFieldEdit?.(segment.schemaKey, segment.targetSchemaFieldName, segment.targetApiFieldName);
        }
        break;
      case 'openWarningAction':
        if (segment.openAction) {
          const metadata = segment.varId ? { varId: segment.varId } : undefined;
          onOpenWarningAction?.(segment.openAction, metadata);
        }
        break;
    }
  };

  // Parse message and render with clickable segments
  const renderMessage = () => {
    if (!segments || segments.length === 0) {
      return <Typography variant="body2">{message}</Typography>;
    }

    // Ensure segments are applied in the order they appear in the message.
    // This avoids "missing link" bugs when tokens arrive out-of-order, and
    // also enables case-insensitive matching for texts like "Add a note".
    const orderedSegments = [...segments].sort((a, b) => {
      const idxA = findSegmentIndex(message, a.text);
      const idxB = findSegmentIndex(message, b.text);
      if (idxA === -1 && idxB === -1) return 0;
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      if (idxA !== idxB) return idxA - idxB;
      // Prefer longer matches first when they start at the same index
      return (b.text?.length || 0) - (a.text?.length || 0);
    });

    const parts: React.ReactNode[] = [];
    let remainingText = message;
    let keyCounter = 0;

    orderedSegments.forEach((segment) => {
      const index = findSegmentIndex(remainingText, segment.text);
      
      if (index === -1) {
        return; // Segment not found in remaining text
      }

      // Add text before the segment
      if (index > 0) {
        const beforeText = remainingText.substring(0, index);
        parts.push(
          <Typography
            key={`text-${keyCounter++}`}
            component="span"
            variant="body2"
            sx={{ color: 'text.primary' }}
          >
            {beforeText}
          </Typography>
        );
      }

      // Add clickable segment
      const matchedText = remainingText.substring(index, index + segment.text.length);
      parts.push(
        <Link
          key={`link-${keyCounter++}`}
          component="span"
          onClick={(e) => {
            // Defensive: avoid any accidental navigation / form side-effects.
            e.preventDefault();
            e.stopPropagation();
            handleSegmentClick(segment);
          }}
          sx={{
            color: theme.palette.primary.main,
            textDecorationLine: 'underline',
            fontWeight: 700,
            cursor: 'pointer',
            fontSize: '14px',
            '&:hover': {
              color: theme.palette.primary.dark,
            },
          }}
        >
          {matchedText}
          {segment.showEditIcon && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                ml: 0.1,
                verticalAlign: 'middle',
              }}
            >
              <EditIcon sx={{ fontSize: '14px' }} />
            </Box>
          )}
        </Link>
      );

      // Update remaining text
      remainingText = remainingText.substring(index + segment.text.length);
    });

    // Add any remaining text
    if (remainingText) {
      parts.push(
        <Typography
          key={`text-${keyCounter++}`}
          component="span"
          variant="body2"
          sx={{ color: 'text.primary' }}
        >
          {remainingText}
        </Typography>
      );
    }

    return <Box sx={{ fontSize: '14px', lineHeight: 1.6 }}>{parts}</Box>;
  };

  return (
    <Box
      sx={{
        borderLeft: `4px solid #3B82F6`,
        backgroundColor: 'transparent',
        padding: '12px 16px',
        borderRadius: '0px',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          mb: 0.5,
          fontSize: '14px',
        }}
      >
        Quick check
      </Typography>
      {renderMessage()}
    </Box>
  );
};

export default QuickCheck;

